import React from "react";
import { ArrowRight, ShieldCheck, Timer, BadgeCheck, FileDown } from "lucide-react";

const SAMPLE_SOA_URL =
  "https://docs.google.com/document/d/10cmFaykQbqo0TsE2U4chucZM1L1N2Ype/edit?usp=drive_link";

const Hero = () => {
  const goBook = (e) => {
    e.preventDefault();
    document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-white border-b-2 border-black"
    >
      <div className="absolute inset-0 hero-grid-bg pointer-events-none" />

      {/* Yellow geometric accents */}
      <div className="hidden md:block absolute top-24 right-10 w-40 h-40 bg-[#FACC15] border-2 border-black rotate-12" aria-hidden />
      <div className="hidden md:block absolute bottom-20 right-44 w-24 h-24 bg-white border-2 border-black -rotate-6 shadow-brutal" aria-hidden />
      <div className="hidden md:block absolute top-1/2 right-1/3 w-16 h-16 bg-[#FACC15] border-2 border-black rounded-full" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 lg:pt-28 pb-20 md:pb-28 lg:pb-32 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 reveal">
          <span
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 bg-white border-2 border-black px-3 py-1 text-xs font-bold uppercase tracking-wider shadow-brutal"
          >
            <span className="w-2 h-2 bg-[#FACC15] border border-black" />
            Contract Paraplanning · For Australian Advisers
          </span>

          <h1
            data-testid="hero-headline"
            className="font-display font-black mt-6 text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter"
          >
            Quick. Compliant. SOAs.
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">Zero Delays.</span>
              <span className="absolute inset-x-0 bottom-1 h-4 md:h-5 bg-[#FACC15] -z-0" />
            </span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-6 text-base md:text-lg text-black/80 max-w-2xl leading-relaxed"
          >
            Clear your advice backlog. <strong>Flat-rate pricing</strong>, direct messaging with a
            senior paraplanner, and <strong>guaranteed BID &amp; RG90 compliance</strong>. So you
            can focus on your clients. Backed by 5+ years and 1,100+ advice documents delivered to
            top Australian practices.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href="#booking"
              onClick={goBook}
              data-testid="hero-primary-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#FACC15] text-black border-2 border-black font-bold uppercase text-sm tracking-wide py-4 px-7 shadow-brutal hover-lift"
            >
              Book Free Discovery Call
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={SAMPLE_SOA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-sample-soa"
              className="inline-flex items-center justify-center gap-2 bg-white text-black border-2 border-black font-bold uppercase text-sm tracking-wide py-4 px-7 hover:bg-black hover:text-white transition-colors"
            >
              <FileDown className="w-4 h-4" />
              Download Sample SOA
            </a>
          </div>

          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
            <li className="flex items-center gap-2 bg-white border-2 border-black px-3 py-2 shadow-brutal">
              <Timer className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-wide">Flat $150 Trial SOA</span>
            </li>
            <li className="flex items-center gap-2 bg-white border-2 border-black px-3 py-2 shadow-brutal">
              <ShieldCheck className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-wide">NDA &amp; SLA Available</span>
            </li>
            <li className="flex items-center gap-2 bg-white border-2 border-black px-3 py-2 shadow-brutal">
              <BadgeCheck className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-wide">CFA L2 · B-Tech CS</span>
            </li>
          </ul>
        </div>

        {/* Right visual block — pure CSS abstract geometric composition */}
        <div className="lg:col-span-5 relative reveal" style={{ animationDelay: "120ms" }}>
          <div className="relative w-full aspect-square max-w-md mx-auto">
            <div className="absolute inset-0 bg-[#FACC15] border-2 border-black shadow-brutal-lg" />
            <div className="absolute inset-6 bg-white border-2 border-black flex items-center justify-center p-6">
              <div className="w-full">
                <div className="flex items-center justify-between border-b-2 border-black pb-3">
                  <span className="text-xs font-bold uppercase tracking-widest">SOA / Build #2026-014</span>
                  <span className="w-2 h-2 bg-[#FACC15] border border-black" />
                </div>
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono">FACT.FIND.PARSED</span>
                    <span className="text-xs font-bold">✓ COMPLETE</span>
                  </div>
                  <div className="h-2 bg-black/5 border border-black">
                    <div className="h-full bg-[#FACC15]" style={{ width: "100%" }} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono">XTOOLS+.MODEL</span>
                    <span className="text-xs font-bold">✓ COMPLETE</span>
                  </div>
                  <div className="h-2 bg-black/5 border border-black">
                    <div className="h-full bg-[#FACC15]" style={{ width: "100%" }} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono">SMSF.LRBA.STRATEGY</span>
                    <span className="text-xs font-bold">82%</span>
                  </div>
                  <div className="h-2 bg-black/5 border border-black">
                    <div className="h-full bg-[#FACC15]" style={{ width: "82%" }} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono">BID/RG90.AUDIT</span>
                    <span className="text-xs font-bold">QUEUED</span>
                  </div>
                  <div className="h-2 bg-black/5 border border-black">
                    <div className="h-full bg-[#FACC15]" style={{ width: "30%" }} />
                  </div>
                </div>
                <div className="mt-6 border-t-2 border-black pt-3 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-widest">ETA</span>
                  <span className="text-[10px] font-bold">36h 12m</span>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -right-6 w-20 h-20 bg-black border-2 border-black rotate-6" aria-hidden />
            <div className="absolute -top-6 -left-6 w-16 h-16 border-2 border-black bg-white rounded-full" aria-hidden />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
